/**
 * Gerenciador de Eventos C# → Frontend
 * 
 * Responsável por:
 * - Registrar handlers para eventos específicos
 * - Despachar eventos quando chegarem do backend
 * - Gerenciar múltiplos handlers por evento
 * - Remover handlers quando necessário
 * 
 * Eventos são mensagens espontâneas do backend que NÃO têm requestId
 * e NÃO requerem resposta do frontend.
 * 
 * @module EventManager
 */

import { sendToTerminal, sendToTestLog } from '../../../terminalLogger';
import { LogCategory } from '@/config/logging';

export type EventHandler = (data: any) => void;

/**
 * Gerenciador de eventos do backend C#
 */
export class EventManager {
  private handlers: Map<string, Set<EventHandler>> = new Map();

  /**
   * Registra um handler para uma action específica
   * 
   * Exemplo:
   * ```typescript
   * eventManager.registerHandler('monitorar-objetos', (data) => {
   *   console.log('Objeto mudou:', data);
   * });
   * ```
   * 
   * @param action Nome da action do evento
   * @param handler Função a ser executada quando evento chegar
   */
  public registerHandler(action: string, handler: EventHandler): void {
    if (!this.handlers.has(action)) {
      this.handlers.set(action, new Set());
    }

    this.handlers.get(action)!.add(handler);

    sendToTerminal('📝 [EVENT_MANAGER] Handler registrado', {
      action,
      totalHandlers: this.handlers.get(action)!.size
    }, LogCategory.EVENT);
  }

  /**
   * Remove um handler específico de uma action
   * 
   * @param action Nome da action
   * @param handler Handler a ser removido
   */
  public unregisterHandler(action: string, handler: EventHandler): void {
    const actionHandlers = this.handlers.get(action);
    if (!actionHandlers) {
      sendToTerminal('⚠️ [EVENT_MANAGER] Handler não encontrado', { action }, LogCategory.EVENT);
      return;
    }

    const removed = actionHandlers.delete(handler);
    
    // Se não há mais handlers, remover a action do mapa
    if (actionHandlers.size === 0) {
      this.handlers.delete(action);
    }

    sendToTerminal('📝 [EVENT_MANAGER] Handler removido', {
      action,
      removed,
      remainingHandlers: actionHandlers.size
    }, LogCategory.EVENT);
  }

  /**
   * Remove todos os handlers de uma action específica
   * 
   * @param action Nome da action
   */
  public unregisterAllHandlers(action: string): void {
    const actionHandlers = this.handlers.get(action);
    if (!actionHandlers) {
      sendToTerminal('⚠️ [EVENT_MANAGER] Nenhum handler para remover', { action }, LogCategory.EVENT);
      return;
    }

    const count = actionHandlers.size;
    this.handlers.delete(action);

    sendToTerminal('📝 [EVENT_MANAGER] Todos os handlers removidos', {
      action,
      count
    }, LogCategory.EVENT);
  }

  /**
   * Despacha um evento para todos os handlers registrados
   * 
   * @param action Nome da action
   * @param data Dados do evento
   */
  public dispatch(action: string, data: any): void {
    const actionHandlers = this.handlers.get(action);
    
    if (!actionHandlers || actionHandlers.size === 0) {
      sendToTerminal('⚠️ [EVENT_MANAGER] Nenhum handler registrado', {
        action,
        hasData: !!data
      }, LogCategory.EVENT);
      return;
    }

    // 🔇 Logs de despacho removidos para reduzir verbosidade

    // Executar cada handler em sequência
    for (const handler of actionHandlers) {
      try {
        handler(data);
      } catch (error) {
        sendToTerminal('❌ [EVENT_MANAGER] Erro no handler', {
          action,
          error: error instanceof Error ? error.message : String(error)
        }, LogCategory.ERROR);
      }
    }
  }

  /**
   * Remove todos os handlers de todas as actions
   */
  public clear(): void {
    const totalActions = this.handlers.size;
    let totalHandlers = 0;

    for (const handlers of this.handlers.values()) {
      totalHandlers += handlers.size;
    }

    this.handlers.clear();

    sendToTerminal('🧹 [EVENT_MANAGER] Todos os handlers limpos', {
      totalActions,
      totalHandlers
    }, LogCategory.EVENT);
  }

  /**
   * Retorna informações sobre handlers registrados
   */
  public getStats(): {
    totalActions: number;
    totalHandlers: number;
    actions: Array<{ action: string; handlerCount: number }>;
  } {
    const actions: Array<{ action: string; handlerCount: number }> = [];
    let totalHandlers = 0;

    for (const [action, handlers] of this.handlers.entries()) {
      const count = handlers.size;
      actions.push({ action, handlerCount: count });
      totalHandlers += count;
    }

    return {
      totalActions: this.handlers.size,
      totalHandlers,
      actions
    };
  }

  /**
   * Verifica se existe algum handler registrado para uma action
   */
  public hasHandlers(action: string): boolean {
    const handlers = this.handlers.get(action);
    return !!handlers && handlers.size > 0;
  }
}

